import { useEffect, useState } from 'react';
import { AlertTriangle, Clock, X, Zap, Shield } from 'lucide-react';
import useAuthStore from '../stores/authStore';
import useRaLicenseStore from '../stores/raLicenseStore';

const RaLicenseBanner = () => {
  const { tenantVersion } = useAuthStore();
  const { license, fetchLicense } = useRaLicenseStore();
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    fetchLicense();
    setDismissed(false);
  }, [tenantVersion, fetchLicense]);

  if (!license || dismissed) return null;

  const {
    status, sku, maxCameras, currentCameras,
    trialDaysRemaining, isTrialExpired,
  } = license;

  const isExpired = status === 'EXPIRED' || isTrialExpired;
  const isOverLimit = typeof currentCameras === 'number' && currentCameras > maxCameras;
  const percent = maxCameras > 0 ? (currentCameras / maxCameras) * 100 : 0;
  const isNearLimit = !isOverLimit && percent >= 80;

  if (isExpired) {
    return (
      <div className="md-license-banner ra-banner expired">
        <div className="md-license-banner-content">
          <AlertTriangle size={18} />
          <span>
            {sku === 'RA_TRIAL'
              ? 'Your Recording Assurance trial has expired. Upgrade to continue verifying your cameras.'
              : 'Your Recording Assurance license has expired. Please contact your administrator to renew.'}
          </span>
        </div>
        <div className="md-license-banner-actions">
          <button
            className="md-license-btn upgrade"
            onClick={() => window.open('https://serverli.com/pricing', '_blank')}
          >
            <Zap size={14} /> Upgrade Now
          </button>
        </div>
      </div>
    );
  }

  if (status === 'TRIAL' && trialDaysRemaining !== null && trialDaysRemaining !== undefined) {
    const urgent = trialDaysRemaining <= 2;
    return (
      <div className={`md-license-banner ra-banner trial ${urgent ? 'urgent' : ''}`}>
        <div className="md-license-banner-content">
          <Clock size={18} />
          <span>
            <strong>{trialDaysRemaining} day{trialDaysRemaining !== 1 ? 's' : ''}</strong> remaining in your Recording Assurance trial.
            {typeof currentCameras === 'number' && ` Monitoring ${currentCameras} of ${maxCameras} cameras.`}
          </span>
        </div>
        <div className="md-license-banner-actions">
          <button
            className="md-license-btn upgrade"
            onClick={() => window.open('https://serverli.com/pricing', '_blank')}
          >
            <Zap size={14} /> Upgrade
          </button>
          <button className="md-license-btn dismiss" onClick={() => setDismissed(true)}>
            <X size={14} />
          </button>
        </div>
      </div>
    );
  }

  if (status === 'SUSPENDED') {
    return (
      <div className="md-license-banner ra-banner expired">
        <div className="md-license-banner-content">
          <Shield size={18} />
          <span>Your Recording Assurance license is suspended. Contact support to restore access.</span>
        </div>
      </div>
    );
  }

  if (isOverLimit) {
    return (
      <div className="md-license-banner ra-banner warning">
        <div className="md-license-banner-content">
          <AlertTriangle size={18} />
          <span>
            You've exceeded your camera limit ({currentCameras}/{maxCameras}).
            Upgrade your plan to monitor more cameras.
          </span>
        </div>
        <div className="md-license-banner-actions">
          <button
            className="md-license-btn upgrade"
            onClick={() => window.open('https://serverli.com/pricing', '_blank')}
          >
            <Zap size={14} /> Upgrade
          </button>
          <button className="md-license-btn dismiss" onClick={() => setDismissed(true)}>
            <X size={14} />
          </button>
        </div>
      </div>
    );
  }

  if (isNearLimit) {
    return (
      <div className="md-license-banner ra-banner info">
        <div className="md-license-banner-content">
          <AlertTriangle size={18} />
          <span>You're approaching your camera limit ({currentCameras}/{maxCameras}).</span>
        </div>
        <div className="md-license-banner-actions">
          <button className="md-license-btn dismiss" onClick={() => setDismissed(true)}>
            <X size={14} />
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default RaLicenseBanner;
